import React from 'react'
import { z } from 'zod'

import { Button } from '../../catalyst/button'
import { Checkbox, CheckboxField } from '../../catalyst/checkbox'
import { Label } from '../../catalyst/fieldset'
import { Heading } from '../../catalyst/heading'
import { Strong, Text, TextLink } from '../../catalyst/text'
import { Logo } from '../../components/Logo'
import { schemas, TextField, useNativeFormSubmit, useValidatedForm } from '../../components/forms'

interface LoginProps {
  actionUrl: string
  forgotPasswordUrl: string
  authenticity_token: string
  flash?: Record<string, string>
}

// Sign-in checks shape only. Whether the pair is correct is the server's
// answer, and guessing at it here would leak which half was wrong.
//
// The schema mirrors the params hash Rails receives, because the field's DOM
// name (`user[email]`) and its form path (`user.email`) have to resolve to the
// same place — see `submitName` in components/forms/fields.
const signInSchema = z.object({
  user: z.object({
    email: schemas.email(),
    password: z.string().min(1, 'Password is required.'),
  }),
})

type SignInValues = z.infer<typeof signInSchema>

export function Login({ actionUrl, forgotPasswordUrl, authenticity_token, flash }: LoginProps) {
  const form = useValidatedForm<SignInValues>(signInSchema, {
    defaultValues: { user: { email: '', password: '' } },
  })
  const formProps = useNativeFormSubmit(form)

  return (
    <div className="w-full max-w-sm">
      <form
        action={actionUrl}
        method="POST"
        className="grid w-full grid-cols-1 gap-8"
        {...formProps}
      >
        <input type="hidden" name="authenticity_token" value={authenticity_token} />

        <div>
          <a href="/" aria-label="Home">
            <Logo className="mx-auto h-12 w-auto text-zinc-950 dark:text-white" />
          </a>
          <Heading className="mt-6 text-center">Sign in to your account</Heading>
        </div>

        {flash?.alert && (
          <div
            role="alert"
            className="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/50 dark:text-red-200"
          >
            {flash.alert}
          </div>
        )}
        {flash?.notice && (
          <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/50 dark:text-green-200">
            {flash.notice}
          </div>
        )}

        <TextField
          form={form}
          name="user.email"
          submitName="user[email]"
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
        />

        <TextField
          form={form}
          name="user.password"
          submitName="user[password]"
          label="Password"
          type="password"
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <CheckboxField>
            <Checkbox name="user[remember_me]" value="1" />
            <Label>Remember me</Label>
          </CheckboxField>
          <Text>
            <TextLink href={forgotPasswordUrl}>
              <Strong>Forgot password?</Strong>
            </TextLink>
          </Text>
        </div>

        <Button type="submit" className="w-full">
          Sign in
        </Button>
      </form>
    </div>
  )
}
